import React, { useCallback, useEffect, useState, useId, memo } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import { BREAKPOINTS, classNames } from '../../utils';
import { useMediaQuery } from '../../react-hooks/use-media-query';
import Websites from './websites';
import {
  getQueryStringValue,
  setQueryStringValue,
  useQueryString
} from '../../react-hooks/use-query-string';

function Facets({
  appliedFiltersLabel,
  activeFacets,
  data,
  websites,
  isOpen,
  setIsOpen,
  setActiveFacets,
  setPage,
  clearAllLabel
}) {
  const id = useId();
  const isDesktop = useMediaQuery(
    `(min-width: ${BREAKPOINTS.TABLET_LANDSCAPE}px)`
  );
  const [facetsQs, setFacetsQs] = useState({});
  const [, updateState] = useState();
  const forceUpdate = useCallback(() => updateState({}), []);

  const updateFacets = useCallback(
    (keyword) => {
      const value = getQueryStringValue(keyword);

      setFacetsQs((prev) => ({
        ...prev,
        [keyword]: value ?? []
      }));
    },
    [setFacetsQs]
  );

  const appliedFilters = Object.entries(activeFacets)
    .filter(([k]) => k !== 'website')
    .map(([k, v]) => (Array.isArray(v) ? [k, v] : [k, [v]]))
    .flatMap(([k, v]) =>
      v.map((i) => {
        const category = data?.find((item) => item?.Value === k);

        return {
          key: k,
          value: i,
          categoryLabel: category?.Label,
          label: category?.Items.find((item) => item?.Value === i)?.Label
        };
      })
    );

  const handleFacetRemove = useCallback(
    (key, value) => {
      const event = new CustomEvent('popstate');
      const active = (() => {
        const facets = getQueryStringValue(key);
        if (Array.isArray(facets)) return facets;
        return facets ? [facets] : [];
      })();
      setQueryStringValue(
        key,
        active.filter((v) => v !== value)
      );
      setActiveFacets((prev) => ({
        ...prev,
        [key]: active.filter((v) => v !== value)
      }));
      setPage('');
      window.dispatchEvent(event);
    },
    [setActiveFacets, setPage]
  );

  const clearAllFacets = useCallback(() => {
    const event = new CustomEvent('popstate');
    Object.keys(activeFacets).forEach((key) => {
      setQueryStringValue(key, []);
      setActiveFacets((prev) => ({
        ...prev,
        [key]: []
      }));
      setPage('');
    });
    window.dispatchEvent(event);
  }, [setActiveFacets, setPage, activeFacets]);

  useEffect(() => {
    if (Object.keys(facetsQs).length) setActiveFacets(facetsQs);
  }, [facetsQs]);

  return (
    <>
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        className={classNames(
          'sm-only search-listing__filters-button',
          isOpen && 'search-listing__filters-button--expanded'
        )}
        id={`trigger-${id}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        Narrow result
      </button>
      <AnimateHeight
        aria-labelledby={`trigger-${id}`}
        className="search-listing__facets"
        id={`panel-${id}`}
        role="region"
        height={isOpen || isDesktop ? 'auto' : 0}
      >
        {websites ? <Websites websites={websites} /> : null}
        {data?.map((facet, index) => (
          <Facet
            facet={facet}
            key={index}
            index={index}
            updateFacets={updateFacets}
            setPage={setPage}
            forceUpdate={forceUpdate}
          />
        ))}
        {appliedFilters.length ? (
          <div className="search-listing__selected-facets">
            <h2 className="search-listing__selected-facets-title">
              <p>{appliedFiltersLabel}</p>
              <button className="link" onClick={clearAllFacets}>
                {clearAllLabel}
              </button>
            </h2>
            <ul role="list">
              {appliedFilters.map(
                ({ key, value, categoryLabel, label }, index) => (
                  <li key={index}>
                    <button
                      className="search-listing__selected-facet"
                      onClick={() => handleFacetRemove(key, value)}
                      aria-label={`Remove Facet - ${categoryLabel}: ${label}`}
                    >
                      {categoryLabel}: {label}
                      <span>×</span>
                    </button>
                  </li>
                )
              )}
            </ul>
          </div>
        ) : null}
      </AnimateHeight>
    </>
  );
}

Facets.propTypes = {
  appliedFiltersLabel: PropTypes.string,
  clearAllLabel: PropTypes.string,
  activeFacets: PropTypes.object,
  setPage: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setActiveFacets: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      Label: PropTypes.string,
      Value: PropTypes.string,
      Items: PropTypes.arrayOf(
        PropTypes.shape({
          Count: PropTypes.number,
          Label: PropTypes.string,
          Value: PropTypes.string
        })
      )
    })
  ),
  websites: PropTypes.shape({
    Label: PropTypes.string,
    Items: PropTypes.arrayOf(
      PropTypes.shape({
        Label: PropTypes.string,
        Count: PropTypes.number,
        Url: PropTypes.string,
        Current: PropTypes.bool
      })
    )
  })
};

function Facet({ facet, updateFacets, setPage, forceUpdate, index }) {
  const [facetQs, setFacetQs] = useQueryString(facet?.Value);
  const getActiveFilters = useCallback(() => {
    if (Array.isArray(facetQs)) return facetQs;
    return facetQs ? [facetQs] : [];
  }, [facetQs]);
  const handleChange = useCallback(
    (val, checked) => {
      if (checked) {
        setFacetQs([...getActiveFilters(), val]);
      } else {
        setFacetQs(getActiveFilters().filter((f) => f !== val));
      }
      setPage('');
      forceUpdate();
    },
    [getActiveFilters, setFacetQs, updateFacets, facet?.Value]
  );

  useEffect(() => {
    updateFacets(facet?.Value);
  }, [facetQs]);

  return (
    <div
      className={classNames(
        'search-facet-group',
        facet?.Items.length > 8 && 'search-facet-group--scrollable'
      )}
    >
      <h2 className="search-facet-group__title">{facet?.Label}</h2>
      <ul role="list">
        {facet?.Items?.map((item) => {
          const id = `${item?.Value}-${index}`;
          return (
            <li key={item?.Value}>
              <div className="checkbox">
                <input
                  type="checkbox"
                  className="checkbox__input"
                  id={id}
                  onChange={(e) => handleChange(item?.Value, e.target.checked)}
                  checked={getActiveFilters().includes(item?.Value)}
                />
                <label className="checkbox__label" htmlFor={id}>
                  {item?.Label} ({item?.Count})
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

Facet.propTypes = {
  forceUpdate: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  updateFacets: PropTypes.func.isRequired,
  facet: PropTypes.shape({
    Label: PropTypes.string.isRequired,
    Value: PropTypes.string.isRequired,
    Items: PropTypes.arrayOf(
      PropTypes.shape({
        Label: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired,
        Info: PropTypes.oneOfType([
          PropTypes.string.isRequired,
          PropTypes.oneOf([null]).isRequired
        ])
      })
    ).isRequired
  }).isRequired
};

export default memo(Facets);
